// lib
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { MAPPING_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'

// components
import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

import DropTargetContainer from '@aytm/app/stats/page/sidebar/components/DropTargetContainer'

import CustomQuestionButton from '@aytm/app/stats/page/sidebar/components/CustomQuestionButton'
import PinnedCustomQuestion from '@aytm/app/stats/page/sidebar/components/PinnedCustomQuestion'

// styles
import style from '@aytm/app/stats/page/sidebar/mapping/styles/MappingPanel.module.scss'

const MappingPanel = ({
  sidebar,
  root,
  data,
  features,
  buttons,
  mappings,
  questions,
  disabled,
  addCustomQuestion,
  addPinnedQuestion,
  removePinnedQuestion,
  onDNDStart,
  onDNDEnd
}) => {
  const acceptTypes = useMemo(
    () =>
      buttons.reduce((acc, cur) => {
        if (!features[cur.feature].disabled) acc.push(cur.adndType)
        return acc
      }, []),
    [buttons, features]
  )

  const typesTitles = useMemo(
    () =>
      buttons.reduce((acc, cur) => {
        if (!features[cur.feature].disabled) acc[cur.adndType] = cur.text
        return acc
      }, {}),
    [buttons, features]
  )

  return (
    <div className={style.component}>
      <div className={style['component-title']}>
        Insights Portal Mapping
        <SidebarPopup id="mapping" root={root} help={MAPPING_HELP} width="300px" />
      </div>

      <DropTargetContainer
        root={sidebar}
        acceptTypes={acceptTypes}
        typesTitles={typesTitles}
        windowUID={data.windowUID}
        onDNDDrop={addPinnedQuestion}
      >
        <div className={`${style.row} ${style.paragraph} p-fluid`}>
          Add data from this survey to Insights Portal by creating a new mapping or editing an existing mapping. You can
          send concepts, attributes, and variables to your portals.
        </div>
        <div className={`${style.row} ${style['section-title']} p-fluid`}>Create new mapping</div>
        {buttons.map(item =>
          features[item.feature].hidden ? null : (
            <CustomQuestionButton
              key={item.type}
              root={root}
              item={item}
              features={features}
              windowUID={data.windowUID}
              disabled={disabled}
              addCustomQuestion={addCustomQuestion}
              onDNDStart={onDNDStart}
              onDNDEnd={onDNDEnd}
            />
          )
        )}
        {0 < mappings.length && (
          <>
            <div className={`${style.row} ${style['section-title']} p-fluid`}>Saved mappings</div>
            <div className={`${style.row} ${style.note} p-fluid`}>
              Note: if a mapping is deleted from the report it&apos;s saved in, it will also be permanently removed from
              this list of saved mappings.
            </div>
            {mappings.map(item => (
              <PinnedCustomQuestion
                key={item.key}
                item={item}
                features={features}
                disabled={disabled}
                addCustomQuestion={addCustomQuestion}
                onDNDStart={onDNDStart}
                onDNDEnd={onDNDEnd}
              />
            ))}
          </>
        )}
        {0 < questions.length && !disabled ? (
          <>
            <div className={`${style.row} ${style['section-title']} p-fluid`}>Pinned mappings</div>
            {questions.map(item => (
              <PinnedCustomQuestion
                key={`question-${item.index}`}
                item={item}
                features={features}
                disabled={disabled}
                addCustomQuestion={addCustomQuestion}
                removePinnedQuestion={removePinnedQuestion}
                onDNDStart={onDNDStart}
                onDNDEnd={onDNDEnd}
              />
            ))}
          </>
        ) : null}
      </DropTargetContainer>
    </div>
  )
}

MappingPanel.propTypes = {
  sidebar: PropTypes.any,
  root: PropTypes.any,
  data: PropTypes.object,
  features: PropTypes.object,
  buttons: PropTypes.array,
  mappings: PropTypes.array,
  questions: PropTypes.array,
  disabled: PropTypes.bool,
  addCustomQuestion: PropTypes.func,
  addPinnedQuestion: PropTypes.func,
  removePinnedQuestion: PropTypes.func,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func
}

MappingPanel.displayName = 'MappingPanel'

export default MappingPanel
